import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getEvents, deleteEvent } from '../../services/api';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from '../common/DataTable';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const EventList = forwardRef(function EventList({ onEdit }, ref) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (error) {
      toast.error('Failed to fetch events');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: fetchEvents
  }));

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteEvent(deleteId);
      toast.success('Event deleted successfully');
      fetchEvents();
      setDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete event');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    { id: 'title', label: 'EVENT TITLE' },
    { id: 'description', label: 'DESCRIPTION' },
    { id: 'location', label: 'LOCATION' },
    {
      id: 'date',
      width: 140,
      label: 'START DATE',
      render: (row) => moment(row.date).format('MMM DD, YYYY')
    },
    {
      id: 'endDate',
      width: 140,
      label: 'END DATE',
      render: (row) => moment(row.endDate).format('MMM DD, YYYY')
    },
    {
      id: 'actions',
      label: 'ACTIONS',
      align: 'right',
      width: 120,
      render: (row) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() => onEdit(row)}
            size="small"
            sx={{
              color: '#94a3b8',
              '&:hover': { color: '#2563eb', bgcolor: '#eff6ff' },
              transition: 'all 0.2s'
            }}
          >
            <EditIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={() => setDeleteId(row.id)}
            size="small"
            sx={{
              color: '#94a3b8',
              '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' },
              transition: 'all 0.2s',
              ml: 1
            }}
          >
            <DeleteIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </div>
      )
    }
  ];

  return (
    <>
      <DataTable
        premium={true}
        columns={columns}
        data={events}
        loading={loading}
        emptyTitle="No Events Found"
        emptySubtitle="There are no upcoming events to display yet."
      />

      <DeleteConfirmDialog
        open={!!deleteId}
        onClose={() => !deleteLoading && setDeleteId(null)}
        onConfirm={confirmDelete}
        itemName="event"
        loading={deleteLoading}
      />
    </>
  );
});

export default EventList;
